import { ViewPart } from '@core/models';
import { Block } from '@core/models/block/block.model';

export const ARTWORK_DATA_FORM_FIELD_PREFIX = 'artworksData';
export const PERSONAL_DATA_FILES_FORM_FIELD = 'personalData:main_form_files';
export const PERSONAL_DATA_MEDIA_FORM_FIELD = 'personalData:media_ids';

// zamienia płaski obiekt formularza (z kluczami "a:b:c") na zagnieżdżoną strukturę
export function nestObject(flat: Record<string, any>): Record<string, any> {
  const nested: Record<string, any> = {};

  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split(':');
    let current: any = nested;

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;
      const isIndex = /^\d+$/.test(part);
      const idx = isIndex ? parseInt(part, 10) : part;

      if (isLast) {
        current[idx] = value;
      } else {
        if (!(idx in current)) {
          const nextPart = parts[index + 1];
          const nextIsIndex = /^\d+$/.test(nextPart);
          current[idx] = nextIsIndex ? [] : {};
        }
        current = current[idx];
      }
    });
  }

  return nested;
}


// sprawdza czy dany blok jest sekcją typu "ARTWORK_DATA_FORM_PART"
// kontener formularza danych pracy
export function isArtworkDataPart(item: Block): boolean {
  return item.settings.viewPart === ViewPart.ARTWORK_DATA_FORM_PART;
}

// sprawdza czy dany blok jest sekcją typu "ARTWORK_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER"
// kontener pojedynczej miniatury
export function isArtworksDataUploadSingleThumbnailContainer(item: Block): boolean {
  return item.settings.viewPart === ViewPart.ARTWORK_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER;
}

// sprawdza czy dany blok jest sekcją typu "PERSONAL_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER"
// kontener pojedynczej miniatury
export function isPersonalDataUploadSingleThumbnailContainer(item: Block): boolean {
  return item.settings.viewPart === ViewPart.PERSONAL_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER;
}

// sprawdza czy dany blok jest sekcją typu "ARTWORK_DATA_UPLOAD_THUMBNAILS_CONTAINER"
// kontener miniatur
export function isArtworksDataUploadThumbnailsContainer(item: Block): boolean {
  return item.settings.viewPart === ViewPart.ARTWORK_DATA_UPLOAD_THUMBNAILS_CONTAINER;
}

// sprawdza czy dany blok jest sekcją typu "PERSONAL_DATA_UPLOAD_THUMBNAILS_CONTAINER"
// kontener miniatur
export function isPersonalDataUploadThumbnailsContainer(item: Block): boolean {
  return item.settings.viewPart === ViewPart.PERSONAL_DATA_UPLOAD_THUMBNAILS_CONTAINER;
}

// sprawdza czy dany blok jest sekcją typu "ARTWORK_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER_WITH_PROGRESS"
// kontener miniatury i progressbara
export function isArtworksDataUploadSingleThumbnailContainerWithProgress(item: Block): boolean {
  return item.settings.viewPart === ViewPart.ARTWORK_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER_WITH_PROGRESS;
}

// sprawdza czy dany blok jest sekcją typu "PERSONAL_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER_WITH_PROGRESS"
// kontener miniatury i progressbara
export function isPersonalDataUploadSingleThumbnailContainerWithProgress(item: Block): boolean {
  return item.settings.viewPart === ViewPart.PERSONAL_DATA_UPLOAD_SINGLE_THUMBNAIL_CONTAINER_WITH_PROGRESS;
}

export function fileToBase64(file: File, maxWidth: number = null): Promise<string> {
  const normalizeDimensions = (width: number, height: number) => {
    if (maxWidth !== null && maxWidth > 0) {
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
    }
    return {width, height};
  };

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = reject;

    if (file.type.startsWith('image/')) {
      // ----- OBRAZ -----
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const {width, height} = normalizeDimensions(img.width, img.height);
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d')!;
          ctx.drawImage(img, 0, 0, width, height);

          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);

    } else if (file.type.startsWith('video/')) {
      // ----- WIDEO -----
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');

      video.preload = 'metadata';
      video.src = url;

      video.onloadedmetadata = () => {
        // 10% długości
        video.currentTime = video.duration * 0.1;
      };

      video.onseeked = () => {
        const {videoWidth, videoHeight} = video;

        const {width, height} = normalizeDimensions(videoWidth, videoHeight);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(video, 0, 0, width, height);

        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL('image/png'));
      };

      video.onerror = reject;

    } else {
      // reject(new Error("Unsupported file type"));
      resolve(null);
    }
  });
}

export function getMediaFieldName(index: number | string): string {
  return `${ARTWORK_DATA_FORM_FIELD_PREFIX}:${index}:media_ids`;
}
